import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { getUserData, logout as utilLogout } from "../utils/utility";
import { RolePermission } from "../redux/Slices/BusAdmin/BusAdmin.util";
import { useTypedSelector } from "../redux/redux-hooks";
import { LOCAL_STORAGE_TOKEN_KEY } from "../constants/keys";

interface User {
  id: number;
  role: string;
  permissions: RolePermission[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  hasPermission: (feature: string, action: string) => boolean;
  hasFeature: (feature: string | string[]) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Watch Redux login state for changes
  const { login, rolePermissions } = useTypedSelector(
    (state) => state.bus_admin
  );

  // Initialize from sessionStorage on mount
  useEffect(() => {
    const userData = getUserData();
    const storedToken = sessionStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);

    if (userData && storedToken) {
      setUser({
        id: userData.id,
        role: userData.role,
        permissions: userData.permissions || [],
      });
      setToken(storedToken);
    }

    // Mark as initialized after checking sessionStorage
    setIsInitialized(true);
  }, []);

  // Sync with Redux when login successful
  useEffect(() => {
    if (login.successful && login.payload) {
      setToken(login.payload.access_token);

      if (login.payload.permissions) {
        setUser({
          id: login.payload.id,
          role: login.payload.role,
          permissions: login.payload.permissions,
        });
      }
    }
  }, [login.successful, login.payload]);

  useEffect(() => {
    if (
      rolePermissions.successful &&
      rolePermissions.payload &&
      login.payload
    ) {
      setUser({
        id: login.payload.id,
        role: login.payload.role,
        permissions: rolePermissions.payload,
      });
    }
  }, [rolePermissions.successful, rolePermissions.payload]);

  const hasPermission = useCallback(
    (feature: string, action: string): boolean => {
      if (!user || !user.permissions) return false;

      const permission = user.permissions.find((p) => p.feature === feature);
      if (!permission) return false;

      return permission.actions.some(
        (a) => a.action === action && a.access === true
      );
    },
    [user]
  );

  const hasFeature = useCallback(
    (feature: string | string[]): boolean => {
      if (!user || !user.permissions) return false;

      if (Array.isArray(feature)) {
        return user.permissions.some(
          (p) =>
            feature.includes(p.feature) &&
            p.actions.some((a) => a.access === true)
        );
      }

      const permission = user.permissions.find((p) => p.feature === feature);
      if (!permission) return false;

      // Check if user has any permission with access=true for this feature
      return permission.actions.some((a) => a.access === true);
    },
    [user]
  );

  const logout = useCallback(() => {
    utilLogout();
    setUser(null);
    setToken(null);
    window.location.href = "/login";
  }, []);

  const isAuthenticated = !!(user && token);

  // Don't render children until auth state is initialized
  // This prevents layout shifts when permissions load
  if (!isInitialized) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        hasPermission,
        hasFeature,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
